import { Box, Checkbox, styled, useTheme, Typography, useMediaQuery, Stack } from '@mui/material';
import { TSocialMedia } from 'src/models/general';
import ButtonCircle from '../ButtonCircle';
import GetSocialMediaIcon from '../GetSocialMediaIcon';
import { themeColors } from 'src/theme/schemes/PureLightTheme';

const Container = styled(Box)(
  () => `
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 6px 9px 6px 9px;
    border-radius:18px;
    background: #FBFBFB;
    border: 1px solid #E8E9EA;
    box-sizing: border-box;
  `
);

const CustomCheckbox = styled(Checkbox)<{ activeBgColor: string, matchesUpMd: boolean }>(
  ({ theme, activeBgColor, matchesUpMd }) => `
    padding: ${theme.spacing(1)} 0px ${theme.spacing(1)} 2px;
    width: ${theme.spacing(5)};
    height: ${theme.spacing(3)};
    border-radius: ${theme.spacing(1.43)};
    padding: 0;
    margin: 0;

    &.Mui-checked {
      background-color: ${activeBgColor};
      color: white;
    }

    &:not(.Mui-checked) {
      background-color: ${theme.palette.grey[100]};
      border: 1px solid ${theme.palette.grey[300]};
      color: ${theme.palette.grey[600]};
    }

    &:hover {
      background-color: ${activeBgColor};
      opacity: 0.8;
    }

    &:not(.Mui-checked):hover {
      background-color: ${theme.palette.grey[200]};
      opacity: 1;
    }

    .MuiSvgIcon-root {
      font-size: ${matchesUpMd ? "16px" : "12px"}
    }
  `
);

interface Props {
  selectedPlatforms: TSocialMedia[];
  handleChange: (platform: TSocialMedia) => void;
  handleClickAll: () => void;
  socialMediaList?: TSocialMedia[];
}

export const SOCIAL_MEDIA_LIST: TSocialMedia[] = [
  'youtube',
  'twitter',
  'facebook',
  'instagram',
  'tiktok',
  'linkedin',
  // 'googleReview',
  // 'telegram',
  // 'whatsapp',
  'threads',
  'reddit'
];

const FilterObjectByPlatform = ({
  selectedPlatforms,
  handleChange,
  handleClickAll,
  socialMediaList = SOCIAL_MEDIA_LIST
}: Props) => {
  const theme = useTheme();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSelected = (platform: TSocialMedia) =>
    selectedPlatforms.includes(platform);

  const getSocialMediaBgColor = (platform: TSocialMedia) => {
    switch (platform) {
      case 'youtube':
        return '#FF0000';
      case 'twitter':
        return '#000000';
      case 'facebook':
        return '#3B5998';
      case 'instagram':
        return '#7A3EAE';
      case 'tiktok':
        return '#051C26';
      case 'linkedin':
        return '#0077B5';
      case 'telegram':
        return '#0088CC';
      case 'whatsapp':
        return '#25D366';
      case 'threads':
        return themeColors.threads;
      case 'reddit':
        return themeColors.reddit;
      default:
        return '#E0F6FF';
    }
  };

  return (
    <Stack direction='row'>
      {!matchesUpMd && 
        <Box sx={{display: 'flex', alignItems: 'center', mr: 2}}>
          <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: 999, width: 32, height: 32, border: '1px solid rgba(5, 28, 38, 0.08)', background: '#E0F6FF'}}>
            <Box sx={{width: '24px', height: '24px', color: '#009FE3'}}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
              </svg>
            </Box>
          </Box>
        </Box>}
      <Container sx={{flexWrap: 'wrap', background: !matchesUpMd && 'transparent', justifyContent: !matchesUpMd && "flex-start" }}>
        <ButtonCircle
          onClick={handleClickAll}
          sx={{
            marginRight: matchesUpMd ? theme.spacing(2) : '4px',
            background: '#E0F6FF',
            borderRadius: '12px',
            padding: '4px',
            height: 'unset'
          }}
        >
          <Typography
            sx={{ fontSize: matchesUpMd ? '14px' : '11px', color: '#009FE3', fontWeight: 600 }}
          >
            All
          </Typography>
        </ButtonCircle>
        {socialMediaList.map((platform) => (
          <CustomCheckbox
            key={platform}
            activeBgColor={getSocialMediaBgColor(platform)}
            matchesUpMd={matchesUpMd}
            checked={isSelected(platform)}
            onChange={() => handleChange(platform)}
            sx={{ marginRight: matchesUpMd ? theme.spacing(2) : '4px', marginBottom: !matchesUpMd && '4px' }}
            icon={
              <GetSocialMediaIcon
                socialMedia={platform}
                sx={{ fontSize: 16 }}
                isSolidColor={platform === 'googleReview'}
              />
            }
            checkedIcon={
              <GetSocialMediaIcon
                socialMedia={platform}
                sx={{ color: 'white', fontSize: 16 }}
              />
            }
          />
        ))}
      </Container>
    </Stack>
  );
};

export default FilterObjectByPlatform;
